import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, RotateCcw, ThumbsDown, ThumbsUp, X } from "lucide-react";
import QRCode from "qrcode.react";
import SummaryCaptureForm from "@/components/voice-genie/SummaryCapture/SummaryCaptureForm";
import { useInstallAppBanner } from "@/contexts/ui/install-app-banner";
import { getMobilePlatform } from "@/utils/device";
import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
  APP_STORE_BADGE_SRC,
  GOOGLE_PLAY_BADGE_SRC,
} from "@/constants/appStore";
import { useSubmitConversationFeedback } from "@/hooks/conversation/useConversationFeedback";
import { useConversationRecap } from "@/hooks/voice/useConversationRecap";
import { useSummaryCapture } from "@/hooks/voice/useSummaryCapture";
import { trackCallClick } from "@/lib/ga4";
import { cn } from "@/lib/utils";
import { GenieChannelStrip } from "./GenieChannelStrip";
import { GenieMist } from "./GenieMist";
import { PanelSection, POOF_MOTION, ResourceLinks, staggerIn } from "./geniePanelShared";
import { isSafeUrl } from "./infoLinkIcons";

import type { RecapTranscriptTurn } from "@/types/voice/conversation-recap";
import type { ConversationWrapUpProps } from "@/types/voice/genie-details";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import { useLiveAgentOptional } from "../LiveAgentContext";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
/** Three brand-tinted bars that pulse while the recap is being summoned. */
const RecapShimmer = ({ brandColor }: { brandColor: string }) => (
  <div className="space-y-2.5">
    {[0, 1, 2].map((i) => (
      <div key={i} className="flex items-center gap-3">
        <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ background: `${brandColor}55` }} />
        <motion.span
          className="block h-3 rounded-full"
          style={{
            width: `${[88, 72, 80][i]}%`,
            background: `linear-gradient(90deg, ${brandColor}1f, ${brandColor}33, ${brandColor}1f)`,
          }}
          animate={{ opacity: [0.45, 1, 0.45] }}
          transition={{ duration: 1.3, repeat: Infinity, ease: "easeInOut", delay: i * 0.18 }}
        />
      </div>
    ))}
  </div>
);

/**
 * The end-of-conversation wrap-up — the high-intent closing moment. The
 * live-agent surface poofs to this once a call ends (instead of leaving a tiny
 * "rate me" prompt on a blank screen). Reuses the Details panel's shell + section
 * primitives, composed for the close: a warm sign-off, an inline recap of what
 * was covered, a soft email capture, the business's next-step CTAs + take-away
 * resources, an app/QR hand-off, and a quiet one-tap rating.
 *
 * Self-contained dissolve-and-bloom via AnimatePresence — the parent only flips
 * a boolean.
 */
export const ConversationWrapUp = ({
  followUpSlot,
  open,
  onClose,
  brandColor: brandColorProp,
  secondaryColor: secondaryColorProp,
  backgroundStyle,
  textColor,
  darkSurface = false,
  agentName: agentNameProp,
  logoUrl: logoUrlProp,
  resources,
  qrLogoUrl: qrLogoUrlProp,
  agentId: agentIdProp,
  agentUserId: agentUserIdProp,
  conversationId,
  transcript,
  onSummaryCaptured: onSummaryCapturedProp,
  phoneNumber,
  onSwitchToText,
  helpPageUrl,
  emailAddress,
  guideCount,
}: ConversationWrapUpProps) => {
  // Branding + identity fall back to the LiveAgentContext when the caller is
  // inside a LiveAgentProvider (the live-agent page sets them once there).
  const liveAgent = useLiveAgentOptional();
  const brandColor = brandColorProp ?? liveAgent?.brandColor ?? DEFAULT_BRANDING.primaryColor;
  const secondaryColor = secondaryColorProp ?? liveAgent?.secondaryColor;
  const agentName = agentNameProp ?? liveAgent?.agentName;
  const logoUrl = logoUrlProp ?? liveAgent?.logoUrl;
  const qrLogoUrl = qrLogoUrlProp ?? liveAgent?.qrLogoUrl;
  const agentId = agentIdProp ?? liveAgent?.agentId;
  const agentUserId = agentUserIdProp ?? liveAgent?.agentUserId;
  const onSummaryCaptured = onSummaryCapturedProp ?? liveAgent?.onContactCaptured;

  const end = secondaryColor || "#FF4DB8";
  const gradient = `linear-gradient(90deg, ${brandColor} 0%, ${end} 100%)`;
  const [pageUrl, setPageUrl] = useState("");

  const capture = useSummaryCapture({
    agentId,
    agentUserId,
    source: "wrap_up",
    onCaptured: onSummaryCaptured,
  });

  const feedback = useSubmitConversationFeedback();
  const feedbackEnabled = false;
  const [rated, setRated] = useState<"positive" | "negative" | null>(null);

  const recapTranscript = useMemo<RecapTranscriptTurn[]>(
    () =>
      (transcript ?? [])
        .filter((t) => t.message?.trim())
        .map((t) => ({ role: t.isUser ? "user" : "agent", message: t.message })),
    [transcript],
  );
  const recap = useConversationRecap({ conversationId, transcript: recapTranscript, enabled: open });
  const recapBullets = recap.data ?? [];
  const showRecapSection = recap.isLoading || recapBullets.length > 0;

  const closing = resources?.closing ?? null;
  const closingMessage = closing?.message?.trim() || "Here's a quick wrap-up — grab anything you need before you go.";
  const ctas = useMemo(() => (closing?.ctas ?? []).filter((c) => c.label?.trim() && isSafeUrl(c.url)), [closing]);

  const hasResources = useMemo(() => {
    const docs = (resources?.documents ?? []).filter((d) => d.fileUrl && isSafeUrl(d.fileUrl));
    const lnks = (resources?.links ?? []).filter((l) => isSafeUrl(l.url));
    return docs.length > 0 || lnks.length > 0;
  }, [resources]);
  const showKeepGoing = ctas.length > 0 || hasResources;

  useEffect(() => {
    if (typeof window !== "undefined") setPageUrl(window.location.href);
  }, []);

  useEffect(() => {
    if (open && agentId) capture.markShown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agentId]);

  // A QR code is the right hand-off on a desktop and useless on the device the
  // visitor is already holding, so phones get a store badge instead.
  const platform = useMemo(() => getMobilePlatform(), []);
  const onPhone = platform === "ios" || platform === "android";
  const storeUrl = platform === "android" ? GOOGLE_PLAY_URL : APP_STORE_URL;
  const storeBadgeSrc = platform === "android" ? GOOGLE_PLAY_BADGE_SRC : APP_STORE_BADGE_SRC;
  const storeBadgeAlt =
    platform === "android" ? "Get it on Google Play" : "Download on the App Store";

  const telHref = phoneNumber ? `tel:${phoneNumber.replace(/[^\d+]/g, "")}` : null;
  const showChannelExits = Boolean(onSwitchToText) || Boolean(telHref);

  // The install toast also fires at end of call, from the live agent page. Two
  // "get the app" prompts on one screen reads as nagging, so the panel wins
  // while it is open. suppressBanner deliberately does not write the dismissal
  // timestamp, so the toast can still appear later somewhere else.
  const { suppressBanner } = useInstallAppBanner();
  useEffect(() => {
    if (open) suppressBanner();
  }, [open, suppressBanner]);

  // Escape returns to the Genie / start screen.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleRate = (rating: "positive" | "negative") => {
    if (rated || !conversationId) return;
    setRated(rating);
    feedback.mutate({ conversationId, agentId, rating });
  };

  let step = 0;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="conversation-wrapup"
          {...POOF_MOTION}
          className="absolute inset-0 z-[1500] overflow-y-auto"
          style={backgroundStyle ?? { backgroundColor: "#F9FAFC" }}
          role="dialog"
          aria-modal="true"
          aria-label={`Conversation wrap-up${agentName ? ` for ${agentName}` : ""}`}
        >
          {/* Genie mist — the wrap-up condenses out of swirling brand smoke. */}
          <GenieMist brandColor={brandColor} secondaryColor={end} />

          {/* Vertically centered — fills the viewport, but grows + scrolls if taller. */}
          <div className="flex min-h-full w-full items-center justify-center">
            <div className="relative mx-auto w-full max-w-md px-4 py-12 sm:px-6">
              {/* Quiet dismiss — anchored to the top-right of the centered card column. */}
              <button
                type="button"
                onClick={onClose}
                aria-label="Close wrap-up"
                className="absolute right-4 top-4 z-20 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#475569] transition-all hover:text-[#222B45] sm:right-6"
                style={{ border: `1px solid ${brandColor}22`, boxShadow: "0 4px 14px -8px rgba(15,23,42,0.25)" }}
              >
                <X size={16} strokeWidth={2.25} />
              </button>

              {/* Hero — warm sign-off */}
              <motion.div {...staggerIn(step++)} className="mb-5 flex flex-col items-center text-center">
                {logoUrl && (
                  <span
                    className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl"
                    style={{ background: `linear-gradient(135deg, ${brandColor}1a, ${end}1a)` }}
                  >
                    <img src={logoUrl} alt="" className="h-10 w-10 rounded-xl object-contain" />
                  </span>
                )}
                <h2
                  className={cn(
                    "text-[20px] font-bold leading-tight",
                    !textColor && (darkSurface ? "text-[#F1F5F9]" : "text-[#222B45]"),
                  )}
                  style={textColor ? { color: textColor } : undefined}
                >
                  {agentName ? `Thanks for chatting with ${agentName}` : "Thanks for chatting"}
                </h2>
                <p
                  className={cn(
                    "mt-1.5 text-[13px] leading-relaxed",
                    !textColor && (darkSurface ? "text-[#94A3B8]" : "text-[#64748B]"),
                  )}
                  style={textColor ? { color: `color-mix(in srgb, ${textColor} 70%, white)` } : undefined}
                >
                  {closingMessage}
                </p>
              </motion.div>

              <div className="space-y-3">
                {/* Your recap — the email ask rides directly under the bullets
                    once they exist, so sending it reads as the natural next
                    action on the recap rather than a separate form. */}
                {showRecapSection && (
                  <motion.div {...staggerIn(step++)}>
                    <PanelSection
                      genieIcon="clipboard-check"
                      title="Your recap"
                      brandColor={brandColor}
                      secondaryColor={end}
                    >
                      {recap.isLoading ? (
                        <RecapShimmer brandColor={brandColor} />
                      ) : (
                        <ul className="space-y-2.5">
                          {recapBullets.map((bullet, i) => (
                            <li key={i} className="flex gap-3">
                              <span
                                className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full"
                                style={{ background: gradient }}
                              />
                              <span className="text-[14px] leading-relaxed text-[#3D4759]">{bullet}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {followUpSlot === undefined && agentId && recapBullets.length > 0 && (
                        <div className="mt-4 border-t border-[#E9EDF3] pt-4">
                          <p className="mb-2.5 text-[12px] font-semibold uppercase tracking-wide text-[#9AA7B6]">
                            Send these to me
                          </p>
                          <SummaryCaptureForm
                            brandColor={brandColor}
                            secondaryColor={end}
                            values={capture.values}
                            onChange={capture.onChange}
                            expanded={capture.expanded}
                            onToggleExpanded={capture.toggleExpanded}
                            onSubmit={capture.submit}
                            isSubmitting={capture.isSubmitting}
                            submitted={capture.submitted}
                            error={capture.error}
                            hideHeader
                            hideExpandToggle
                            alreadyCapturedEmail={
                              capture.alreadyCaptured && !capture.editing ? capture.capturedEmail : null
                            }
                            onChangeEmail={capture.startEdit}
                          />
                        </div>
                      )}
                    </PanelSection>
                  </motion.div>
                )}

                {/* Get a summary — fallback placement for when there's no recap
                    to hang the ask under (still loading, or nothing to recap). */}
                {followUpSlot !== undefined && followUpSlot}
                {followUpSlot === undefined && agentId && recapBullets.length === 0 && (
                  <motion.div {...staggerIn(step++)}>
                    <div className="rounded-2xl border border-[#E9EDF3] bg-white p-4 shadow-[0_10px_30px_-22px_rgba(15,23,42,0.35)]">
                      <SummaryCaptureForm
                        brandColor={brandColor}
                        secondaryColor={end}
                        values={capture.values}
                        onChange={capture.onChange}
                        expanded={capture.expanded}
                        onToggleExpanded={capture.toggleExpanded}
                        onSubmit={capture.submit}
                        isSubmitting={capture.isSubmitting}
                        submitted={capture.submitted}
                        error={capture.error}
                        alreadyCapturedEmail={
                          capture.alreadyCaptured && !capture.editing ? capture.capturedEmail : null
                        }
                        onChangeEmail={capture.startEdit}
                      />
                    </div>
                  </motion.div>
                )}

                {/* Keep this going — the other channel exits. Every path here
                    carries the same intent as the email ask above: don't let
                    a warm visitor hit a dead end just because voice is done. */}
                {showChannelExits && (
                  <motion.div {...staggerIn(step++)}>
                    <PanelSection
                      genieIcon="chat"
                      title="Keep this going"
                      brandColor={brandColor}
                      secondaryColor={end}
                    >
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {onSwitchToText && (
                          <button
                            type="button"
                            onClick={onSwitchToText}
                            className="flex items-center gap-2.5 rounded-xl border border-[#E9EDF3] bg-white px-3.5 py-3 text-left transition-all hover:-translate-y-[1px] hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.25)]"
                          >
                            <span
                              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg"
                              style={{ background: `${brandColor}14` }}
                            >
                              <GenieIcon name="chat" size={16} color={brandColor} />
                            </span>
                            <span className="text-[13.5px] font-medium text-[#222B45]">Keep going by typing</span>
                          </button>
                        )}
                        {telHref && (
                          <a
                            href={telHref}
                            onClick={() => trackCallClick("conversation_wrap_up")}
                            className="flex items-center gap-2.5 rounded-xl border border-[#E9EDF3] bg-white px-3.5 py-3 text-left transition-all hover:-translate-y-[1px] hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.25)]"
                          >
                            <span
                              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg"
                              style={{ background: `${brandColor}14` }}
                            >
                              <GenieIcon name="phone" size={16} color={brandColor} />
                            </span>
                            <span className="min-w-0">
                              <span className="block text-[13.5px] font-medium text-[#222B45]">Call us</span>
                              <span className="block truncate text-[12px] text-[#64748B]">{phoneNumber}</span>
                            </span>
                          </a>
                        )}
                      </div>
                    </PanelSection>
                  </motion.div>
                )}

                {/* Keep going on the Genie Page — the highest-priority next step when
                    this genie has a live public page: every guide, manual, FAQ and
                    the Genie itself, in one place. Shown above (and takes visual
                    priority over) the business's own CTA/resources block below. */}
                {helpPageUrl && (
                  <motion.div {...staggerIn(step++)}>
                    <a
                      href={helpPageUrl}
                      className="block rounded-2xl p-[1.5px] transition-transform hover:-translate-y-[1px]"
                      style={{ background: gradient }}
                    >
                      <span className="flex items-center justify-between gap-3 rounded-[14px] bg-white px-4 py-3.5">
                        <span className="flex items-center gap-3">
                          <span
                            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl"
                            style={{ background: `${brandColor}12` }}
                          >
                            <GenieIcon
                              name="book-open"
                              size={18}
                              color={brandColor}
                              secondaryColor={end}
                              gradientEnabled
                            />
                          </span>
                          <span className="min-w-0">
                            <span className="block text-[14px] font-semibold text-[#222B45]">
                              Keep going on the Genie Page
                            </span>
                            <span className="block text-[12px] leading-snug text-[#64748B]">
                              {typeof guideCount === "number" && guideCount > 0
                                ? `${guideCount} guides, manuals, FAQ, and this Genie again`
                                : "Guides, manuals, FAQ, and this Genie again"}
                            </span>
                          </span>
                        </span>
                        <ArrowRight size={16} strokeWidth={2.25} style={{ color: brandColor }} />
                      </span>
                    </a>
                  </motion.div>
                )}

                {/* Keep going — business next-step CTAs + take-away resources */}
                {showKeepGoing && (
                  <motion.div {...staggerIn(step++)}>
                    <PanelSection
                      genieIcon="lightbulb"
                      title="Keep going"
                      brandColor={brandColor}
                      secondaryColor={end}
                    >
                      <div className="space-y-2">
                        {ctas.map((cta) => (
                          <a
                            key={cta.id}
                            href={cta.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block rounded-xl p-[1.5px] transition-transform hover:-translate-y-[1px]"
                            style={{ background: gradient }}
                          >
                            <span className="flex items-center justify-between gap-3 rounded-[10px] bg-white px-4 py-3">
                              <span className="text-sm font-semibold text-[#222B45]">{cta.label}</span>
                              <ArrowRight size={16} strokeWidth={2.25} style={{ color: brandColor }} />
                            </span>
                          </a>
                        ))}
                        {hasResources && <ResourceLinks resources={resources} brandColor={brandColor} />}
                      </div>
                    </PanelSection>
                  </motion.div>
                )}

                {/* Open on your phone */}
                {pageUrl && (
                  <motion.div {...staggerIn(step++)}>
                    <PanelSection
                      genieIcon="globe"
                      title="Take it with you"
                      brandColor={brandColor}
                      secondaryColor={end}
                    >
                      <div className="flex items-center gap-4">
                        <p className="flex-1 text-[13px] leading-snug text-[#64748B]">
                          {onPhone
                            ? "Get the Help Genie app and pick this Genie back up anytime."
                            : "Scan to pick this Genie back up from your pocket, anytime."}
                        </p>
                        {onPhone ? (
                          <a
                            href={storeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-shrink-0 transition-transform hover:scale-[1.03]"
                          >
                            <img src={storeBadgeSrc} alt={storeBadgeAlt} className="h-11 w-auto" />
                          </a>
                        ) : (
                          <div className="flex-shrink-0 rounded-xl border border-[#E9EDF3] bg-white p-2">
                            <QRCode
                              value={pageUrl}
                              size={112}
                              level="H"
                              includeMargin
                              imageSettings={
                                qrLogoUrl ? { src: qrLogoUrl, height: 24, width: 24, excavate: true } : undefined
                              }
                            />
                          </div>
                        )}
                      </div>
                    </PanelSection>
                  </motion.div>
                )}
              </div>

              {/* Talk again + slim feedback footer */}
              <motion.div {...staggerIn(step++)} className="mt-5 flex flex-col items-center">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3 text-[15px] font-semibold text-white shadow-lg transition-all hover:scale-[1.02] hover:opacity-95"
                  style={{ background: gradient, boxShadow: `0 10px 28px -10px ${brandColor}80` }}
                >
                  <RotateCcw size={16} strokeWidth={2.5} />
                  Talk again
                </button>

                <div className="mt-4">
                  <GenieChannelStrip
                    brandColor={brandColor}
                    secondaryColor={end}
                    phoneNumber={phoneNumber}
                    emailAddress={emailAddress}
                    variant="wrapup"
                  />
                </div>
                {/* TODO temp turn off of the feedback - turn on if needed */}
                {feedbackEnabled && conversationId && (
                  <div className="mt-4 flex items-center gap-3">
                    {rated ? (
                      <p className="text-[13px] font-medium text-[#64748B]">Thanks for the feedback ✦</p>
                    ) : (
                      <>
                        <span className="text-[13px] text-[#9AA7B6]">Was this helpful?</span>
                        <button
                          type="button"
                          onClick={() => handleRate("positive")}
                          aria-label="Yes, this was helpful"
                          className="flex h-8 w-8 items-center justify-center rounded-full border transition-all hover:scale-110"
                          style={{ borderColor: `${brandColor}33`, color: brandColor }}
                        >
                          <ThumbsUp size={15} strokeWidth={2.25} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRate("negative")}
                          aria-label="No, this was not helpful"
                          className="flex h-8 w-8 items-center justify-center rounded-full border border-[#E5EAF0] text-[#9AA7B6] transition-all hover:scale-110 hover:text-[#64748B]"
                        >
                          <ThumbsDown size={15} strokeWidth={2.25} />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConversationWrapUp;
